import {
  buildOrderedConfig,
  serializeConfig,
  toReviewerInputs,
  toUiReviewer,
  toUiSigner,
} from "components/Submission/helpers/reviewerConfiguration";
import { Reviewer } from "components/Submission/ReviewersEditList";
import {
  ReviewMode,
  SignatureType,
  SubmissionDocumentType,
  SubmissionStatus,
} from "generated-graphql/graphql";
import { computeNeedsDocumentPreparation } from "hooks/submission/needsDocumentPreparation";
import { SubmissionFormData, SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { useReviewerPreparationContext } from "./useReviewerPreparationContext";

type ContextInput = Pick<
  ReturnType<typeof useReviewerPreparationContext>,
  "submission" | "persistedReviewers" | "documents" | "canSendForReview" | "isStaff" | "status"
>;

/** Whether this signer/signature combination needs BoldSign preparation, and whether staff can trigger it. */
function useSigningFlags({
  submission,
  persistedReviewers,
  documents,
  canSendForReview,
  isStaff,
  status,
  signatureType,
  signer,
}: ContextInput & { signatureType: SignatureType; signer: SubmissionReviewerSigner | null }) {
  const isDigitalSignature = signatureType === SignatureType.Digital;
  const needsDocumentPreparation = useMemo(
    () =>
      computeNeedsDocumentPreparation({
        isDigitalSignature,
        isEditMode: true,
        defaultValues: {
          reviewers: persistedReviewers.map((r) => ({ userId: r.user.id, isSigner: r.isSigner })),
        },
        signer,
        submissionData: { submission },
        submissionDocuments: documents
          .filter((d) => d.type === SubmissionDocumentType.Submission)
          .map((d) => ({ documentId: d.document.id, type: d.type })),
      }),
    [isDigitalSignature, persistedReviewers, signer, submission, documents]
  );
  // Match the legacy form's recovery path: if an in-review signing request is
  // missing or was reset, staff must be able to prepare it again without
  // exposing the status-transition action a second time.
  const canPrepareForSigning = useMemo(
    () => canSendForReview || (isStaff && status === SubmissionStatus.InReview),
    [canSendForReview, isStaff, status]
  );

  return { isDigitalSignature, needsDocumentPreparation, canPrepareForSigning };
}

/** When staff change the submission due date, the details dialog offers to shift reviewer due dates by the same delta and persists them in one save. */
function useDueDateShift({
  reviewers,
  setReviewers,
  signer,
}: {
  reviewers: Reviewer[];
  setReviewers: (update: Reviewer[] | ((prev: Reviewer[]) => Reviewer[])) => void;
  signer: SubmissionReviewerSigner | null;
}) {
  return useMemo(
    () => ({
      reviewers: reviewers.map((r) => ({
        id: r.id,
        name: r.name,
        email: r.email,
        dueDate: r.dueDate,
      })),
      onConfirm: (deltaDays: number) => {
        setReviewers((prev) =>
          prev.map((r) =>
            r.dueDate?.isValid ? { ...r, dueDate: r.dueDate.plus({ days: deltaDays }) } : r
          )
        );
      },
      buildReviewerInputs: (deltaDays: number) => {
        const shifted = reviewers.map((r) =>
          r.dueDate?.isValid ? { ...r, dueDate: r.dueDate.plus({ days: deltaDays }) } : r
        );
        return toReviewerInputs(buildOrderedConfig(shifted, signer));
      },
    }),
    [reviewers, signer, setReviewers]
  );
}

/** Local, editable reviewer/signer/signature config, kept in sync with the persisted submission. */
export function useReviewerPreparationConfigState({
  submission,
  persistedReviewers,
  documents,
  canSendForReview,
  isStaff,
  status,
}: ContextInput) {
  const [signatureType, setSignatureType] = useState<SignatureType>(
    submission?.signatureType ?? SignatureType.None
  );
  const [reviewers, setReviewers] = useState<Reviewer[]>(() =>
    persistedReviewers.filter((r) => !r.isSigner).map(toUiReviewer)
  );
  const [signer, setSigner] = useState<SubmissionReviewerSigner | null>(() => {
    const persistedSigner = persistedReviewers.find((r) => r.isSigner);
    return persistedSigner ? toUiSigner(persistedSigner) : null;
  });
  const [reviewRequired, setReviewRequired] = useState<boolean>(submission?.reviewRequired ?? true);
  const [peerReviewRequired, setPeerReviewRequired] = useState<boolean>(
    submission?.peerReviewRequired ?? false
  );
  // The reviewers datagrid drives review mode through a react-hook-form
  // setValue; this local form exists only to satisfy that contract.
  const reviewModeForm = useForm<SubmissionFormData>({
    defaultValues: { reviewMode: submission?.reviewMode ?? ReviewMode.Parallel },
  });
  const reviewMode = reviewModeForm.watch("reviewMode") ?? ReviewMode.Parallel;

  // "No review required" has to persist as no review; these stay out of every
  // payload while the list itself survives in local state, so toggling back
  // before a save restores it. Memoized so the empty case isn't a new array
  // every render, which would retrigger the validation effect in a loop.
  const reviewersToPersist = useMemo(
    () => (reviewRequired ? reviewers : []),
    [reviewRequired, reviewers]
  );
  const peerReviewToPersist = reviewRequired && peerReviewRequired;

  // Mutations await their stepper refetch, so a save reads clean on resolve.
  const persistedConfig = useMemo(() => {
    const persistedSigner = persistedReviewers.find((r) => r.isSigner);
    return serializeConfig({
      reviewers: persistedReviewers.filter((r) => !r.isSigner).map(toUiReviewer),
      signer: persistedSigner ? toUiSigner(persistedSigner) : null,
      signatureType: submission?.signatureType ?? SignatureType.None,
      reviewRequired: submission?.reviewRequired ?? true,
      peerReviewRequired: submission?.peerReviewRequired ?? false,
      reviewMode: submission?.reviewMode ?? ReviewMode.Parallel,
    });
  }, [persistedReviewers, submission]);
  // Compare what a save would actually write, so dropping hidden reviewers
  // doesn't read as a pending change.
  const hasUnsavedChanges =
    serializeConfig({
      reviewers: reviewersToPersist,
      signer,
      signatureType,
      reviewRequired,
      peerReviewRequired: peerReviewToPersist,
      reviewMode,
    }) !== persistedConfig;

  // Re-sync local config only when the persisted config actually changes, so an
  // unrelated refetch doesn't clobber unsaved reviewer/signer edits.
  useEffect(() => {
    setReviewers(persistedReviewers.filter((r) => !r.isSigner).map(toUiReviewer));
    const persistedSigner = persistedReviewers.find((r) => r.isSigner);
    setSigner(persistedSigner ? toUiSigner(persistedSigner) : null);
    setSignatureType(submission?.signatureType ?? SignatureType.None);
    setReviewRequired(submission?.reviewRequired ?? true);
    setPeerReviewRequired(submission?.peerReviewRequired ?? false);
    reviewModeForm.setValue("reviewMode", submission?.reviewMode ?? ReviewMode.Parallel);
    // All values read here are encoded in persistedConfig; setValue is stable.
    // oxlint-disable-next-line exhaustive-deps
  }, [persistedConfig]);

  const { isDigitalSignature, needsDocumentPreparation, canPrepareForSigning } = useSigningFlags({
    submission,
    persistedReviewers,
    documents,
    canSendForReview,
    isStaff,
    status,
    signatureType,
    signer,
  });
  const detailsDueDateShift = useDueDateShift({ reviewers, setReviewers, signer });

  return {
    signatureType,
    setSignatureType,
    reviewers,
    setReviewers,
    signer,
    setSigner,
    reviewRequired,
    setReviewRequired,
    peerReviewRequired,
    setPeerReviewRequired,
    reviewModeForm,
    reviewMode,
    setReviewMode: reviewModeForm.setValue,
    reviewersToPersist,
    peerReviewToPersist,
    hasUnsavedChanges,
    isDigitalSignature,
    needsDocumentPreparation,
    canPrepareForSigning,
    hasStaffReviewer: reviewers.some((r) => r.isStaff),
    detailsDueDateShift,
  };
}
